import {
  CompositeFilterDescriptor,
  filterBy,
  orderBy,
  SortDescriptor
} from '@progress/kendo-data-query';
import {
  Grid,
  GridProps,
  GridRowClickEvent,
  GridSelectionChangeEvent
} from '@progress/kendo-react-grid';
import React, { forwardRef, useEffect, useState } from 'react';
import { LoadingPanel } from '../LoadingPanel';

interface SelectableItem {
  isSelected?: boolean;
}

type Props = {
  items?: (any & SelectableItem)[];
  loading?: boolean;
  uniqueField?: string;
  children: React.ReactNode;
  onItemSelected?: (item: any) => void;
  onItemsSelected?: (items: any[]) => void;
};

export const SelectionGrid = forwardRef<Grid, Props & GridProps>(
  (props, ref) => {
    const {
      data,
      uniqueField = 'id',
      children,
      loading,
      onFilterChange,
      onItemSelected,
      onItemsSelected,
      ...rest
    } = props;
    const [sort, setSort] = useState<SortDescriptor[]>([]);
    const [filter, setFilter] = useState<CompositeFilterDescriptor>({
      logic: 'or',
      filters: []
    });

    const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(0);
    const [allItems, setAllItems] = useState<SelectableItem[]>([]);

    useEffect(() => {
      if (rest.items) {
        setAllItems(filterBy(orderBy(rest.items, sort), filter));
      }
    }, [rest.items, sort, filter]);

    const onSelectionChange = (event: GridSelectionChangeEvent) => {
      const items = allItems.map(item => {
        if (
          item[uniqueField! as keyof typeof item] ===
          event.dataItem[uniqueField!]
        ) {
          item.isSelected = !event.dataItem.isSelected;
        }
        return item;
      });
      setAllItems(items);
    };

    const onRowClick = (event: GridRowClickEvent) => {
      const items = [...allItems];
      const selectedItems: any[] = [];

      const current = items.findIndex(
        item =>
          item[uniqueField! as keyof typeof item] ===
          event.dataItem[uniqueField!]
      );

      let lastIndex = lastSelectedIndex;

      if (!event.nativeEvent.shiftKey) {
        setLastSelectedIndex(current);
        lastIndex = current;
      }

      if (!event.nativeEvent.ctrlKey) {
        items.forEach(item => (item.isSelected = false));
      }

      const select = !event.dataItem.selected;

      for (
        let i = Math.min(lastIndex, current);
        i <= Math.max(lastIndex, current);
        i++
      ) {
        items[i].isSelected = select;
        if (select) {
          selectedItems.push(items[i]);
        }
      }

      onItemSelected?.(event.dataItem);
      onItemsSelected?.(selectedItems);
      setAllItems(items);
    };

    return (
      <React.Fragment>
        <Grid
          ref={ref}
          data={data ? data : allItems}
          total={allItems?.length}
          sortable
          sort={sort}
          filterable={true}
          filter={filter}
          onSortChange={event => {
            setSort(event.sort);
          }}
          onFilterChange={event => {
            setFilter(event.filter);
            onFilterChange && onFilterChange(event);
          }}
          selectedField="isSelected"
          onSelectionChange={onSelectionChange}
          onRowClick={onRowClick}
          {...rest}
        >
          {children}
        </Grid>
        {loading && <LoadingPanel />}
      </React.Fragment>
    );
  }
);
